/**
 * Shared logic for finding bare registered prefixed entity IDs inside
 * markdown text — the single source for both the web app's
 * `EntityLinkMarkdown` linkifier and any server-side markdown-entity
 * resolution (e.g. product-map capability bodies).
 *
 * @module EntityLink
 */

import { EntityPrefix } from './registry.js';

const prefixAlternation = Object.values(EntityPrefix)
  .sort((left, right) => right.length - left.length)
  .join('|');

/** Matches a bare registered prefixed entity ID, e.g. `wf_abc123`. */
export const BARE_ENTITY_ID = new RegExp(
  `(^|[^A-Za-z0-9_])((?:${prefixAlternation})_[1-9A-HJ-NP-Za-km-z]+)(?![A-Za-z0-9_])`,
  'g'
);

/** Matches fenced code blocks, inline code, and existing markdown links — segments to skip. */
export const PROTECTED_MARKDOWN = /(```[\s\S]*?```|`[^`\n]+`|\[[^\]]*\]\([^)]+\))/g;

function shortEntityId(id: string): string {
  if (id.length <= 18) return id;
  const separator = id.indexOf('_');
  return `${id.slice(0, separator + 1)}${id.slice(separator + 1, separator + 9)}…`;
}

/**
 * Splits markdown into segments, tagging each as protected (fenced code,
 * inline code, existing markdown links) or plain. Protected segments must be
 * left untouched by both linkification and entity-id extraction.
 */
function splitProtectedSegments(markdown: string): Array<{ text: string; protected: boolean }> {
  return markdown
    .split(PROTECTED_MARKDOWN)
    .map((text) => ({ text, protected: Boolean(text.match(PROTECTED_MARKDOWN)) }));
}

function linkifyPlainSegment(segment: string): string {
  return segment.replace(BARE_ENTITY_ID, (_match, leading: string, id: string) => {
    return `${leading}[${shortEntityId(id)}](/lookup/${id})`;
  });
}

/**
 * Linkifies bare registered entity IDs while leaving fenced code blocks,
 * inline code, and existing markdown links untouched. Markdown links
 * intentionally route through `/lookup` so org checks and canonical entity
 * URLs stay centralized.
 */
export function linkifyEntityIds(markdown: string): string {
  return splitProtectedSegments(markdown)
    .map(({ text, protected: isProtected }) => (isProtected ? text : linkifyPlainSegment(text)))
    .join('');
}

/**
 * Extracts the bare registered prefixed entity IDs appearing in a markdown
 * string, skipping fenced code blocks, inline code, and existing markdown
 * links. Order-preserving, de-duplicated.
 */
export function extractEntityIds(markdown: string): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const { text, protected: isProtected } of splitProtectedSegments(markdown)) {
    if (isProtected) continue;
    for (const match of text.matchAll(BARE_ENTITY_ID)) {
      const id = match[2];
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
  }
  return ids;
}
